import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Input, Select, Modal, Form, message, Badge, Space, Tooltip, Divider, Statistic, Row, Col } from 'antd';
import { MessageOutlined, FacebookOutlined, InstagramOutlined, SendOutlined, UserAddOutlined, EyeOutlined, CheckOutlined, InboxOutlined, FilterOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;
const { Option } = Select;

const SocialMessaging = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [studentForm] = Form.useForm();

    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replyModalVisible, setReplyModalVisible] = useState(false);
    const [convertModalVisible, setConvertModalVisible] = useState(false);
    const [filters, setFilters] = useState({
        platform: '',
        status: '',
        search: ''
    });

    // Fetch messages
    const { data: messagesData, isLoading: messagesLoading } = useQuery(
        ['social-messages', filters],
        async () => {
            const params = new URLSearchParams();
            if (filters.platform) params.append('platform', filters.platform);
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            const res = await api.get(`/social-messages?${params}`);
            return res.data;
        },
        {
            refetchInterval: 10000, // Refetch every 10 seconds for new messages
            refetchOnMount: 'always'
        }
    );

    // Fetch statistics
    const { data: statsData } = useQuery(
        'social-messages-stats',
        async () => {
            const res = await api.get('/social-messages/stats/overview');
            return res.data;
        },
        {
            refetchInterval: 30000
        }
    );

    // Fetch courses for student conversion
    const { data: coursesData } = useQuery('courses', async () => {
        const res = await api.get('/courses');
        return res.data;
    });

    // Mark as read mutation
    const markAsReadMutation = useMutation(
        async (messageId) => {
            const res = await api.put(`/social-messages/${messageId}/mark-read`);
            return res.data;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('social-messages');
                queryClient.invalidateQueries('social-messages-stats');
            }
        }
    );

    // Reply mutation
    const replyMutation = useMutation(
        async ({ messageId, reply }) => {
            const res = await api.post(`/social-messages/${messageId}/reply`, { reply });
            return res.data;
        },
        {
            onSuccess: () => {
                message.success(t('Reply sent successfully'));
                setReplyModalVisible(false);
                form.resetFields();
                queryClient.invalidateQueries('social-messages');
                queryClient.invalidateQueries('social-messages-stats');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('Failed to send reply'));
            }
        }
    );

    // Convert to student mutation
    const convertMutation = useMutation(
        async (data) => {
            const res = await api.post(`/social-messages/${data.messageId}/convert-to-student`, data);
            return res.data;
        },
        {
            onSuccess: () => {
                message.success(t('Successfully converted to student'));
                setConvertModalVisible(false);
                studentForm.resetFields();
                queryClient.invalidateQueries('social-messages');
                queryClient.invalidateQueries('social-messages-stats');
                queryClient.invalidateQueries('students');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('Failed to convert to student'));
            }
        }
    );

    const handleReply = (record) => {
        setSelectedMessage(record);
        setReplyModalVisible(true);
    };

    const handleConvert = (record) => {
        setSelectedMessage(record);
        studentForm.setFieldsValue({
            firstName: record.senderName.split(' ')[0],
            lastName: record.senderName.split(' ').slice(1).join(' '),
            phone: record.senderPhone,
            email: record.senderEmail
        });
        setConvertModalVisible(true);
    };

    const handleSendReply = async (values) => {
        await replyMutation.mutateAsync({
            messageId: selectedMessage._id,
            reply: values.reply
        });
    };

    const handleConvertToStudent = async (values) => {
        await convertMutation.mutateAsync({
            messageId: selectedMessage._id,
            ...values
        });
    };

    const handleMarkAsRead = async (messageId) => {
        await markAsReadMutation.mutateAsync(messageId);
    };

    const getPlatformIcon = (platform) => {
        switch (platform) {
            case 'facebook':
                return <FacebookOutlined style={{ color: '#1877F2' }} />;
            case 'instagram':
                return <InstagramOutlined style={{ color: '#E4405F' }} />;
            default:
                return <MessageOutlined />;
        }
    };

    const getPlatformColor = (platform) => {
        switch (platform) {
            case 'facebook': return 'blue';
            case 'instagram': return 'magenta';
            default: return 'default';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'orange';
            case 'replied': return 'blue';
            case 'converted': return 'green';
            case 'archived': return 'default';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: t('Platform'),
            dataIndex: 'platform',
            key: 'platform',
            width: 100,
            render: (platform) => (
                <Tag icon={getPlatformIcon(platform)} color={getPlatformColor(platform)}>
                    {platform}
                </Tag>
            ),
            filters: [
                { text: 'Facebook', value: 'facebook' },
                { text: 'Instagram', value: 'instagram' }
            ],
            onFilter: (value, record) => record.platform === value
        },
        {
            title: t('Sender'),
            dataIndex: 'senderName',
            key: 'senderName',
            render: (name, record) => (
                <div>
                    <div style={{ fontWeight: record.isRead ? 'normal' : 'bold' }}>
                        {name}
                    </div>
                    {record.senderPhone && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                            {record.senderPhone}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: t('Message'),
            dataIndex: 'content',
            key: 'content',
            render: (content, record) => (
                <div style={{ maxWidth: 300 }}>
                    <div style={{ fontWeight: record.isRead ? 'normal' : 'bold' }}>
                        {content.length > 100 ? `${content.substring(0, 100)}...` : content}
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                        {new Date(record.timestamp).toLocaleString()}
                    </div>
                </div>
            )
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status, record) => (
                <Space direction="vertical" size="small">
                    <Tag color={getStatusColor(status)}>
                        {t(status.charAt(0).toUpperCase() + status.slice(1))}
                    </Tag>
                    {!record.isRead && (
                        <Badge status="processing" text={t('Unread')} />
                    )}
                </Space>
            ),
            filters: [
                { text: t('Pending'), value: 'pending' },
                { text: t('Replied'), value: 'replied' },
                { text: t('Converted'), value: 'converted' },
                { text: t('Archived'), value: 'archived' }
            ],
            onFilter: (value, record) => record.status === value
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    {!record.isRead && (
                        <Tooltip title={t('Mark as Read')}>
                            <Button
                                type="text"
                                icon={<CheckOutlined />}
                                size="small"
                                onClick={() => handleMarkAsRead(record._id)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t('Reply')}>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            size="small"
                            onClick={() => handleReply(record)}
                            disabled={record.status === 'converted'}
                        >
                            {t('Reply')}
                        </Button>
                    </Tooltip>
                    {record.status !== 'converted' && (
                        <Tooltip title={t('Convert to Student')}>
                            <Button
                                icon={<UserAddOutlined />}
                                size="small"
                                onClick={() => handleConvert(record)}
                            >
                                {t('Convert')}
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <h1>{t('Social Messaging')}</h1>

            {/* Statistics */}
            {statsData && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('Total Messages')}
                                value={statsData.data?.total || 0}
                                prefix={<MessageOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('Pending')}
                                value={statsData.data?.pending || 0}
                                valueStyle={{ color: '#faad14' }}
                                prefix={<InboxOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('Unread')}
                                value={statsData.data?.unread || 0}
                                valueStyle={{ color: '#cf1322' }}
                                prefix={<EyeOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title={t('Converted')}
                                value={statsData.data?.converted || 0}
                                valueStyle={{ color: '#3f8600' }}
                                prefix={<UserAddOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            <Card>
                {/* Filters */}
                <Space style={{ marginBottom: 16 }} wrap>
                    <Input.Search
                        placeholder={t('Search by name, phone, or message')}
                        allowClear
                        style={{ width: 300 }}
                        onSearch={(value) => setFilters(prev => ({ ...prev, search: value }))}
                    />
                    <Select
                        placeholder={t('Filter by platform')}
                        allowClear
                        style={{ width: 150 }}
                        onChange={(value) => setFilters(prev => ({ ...prev, platform: value || '' }))}
                    >
                        <Option value="facebook">
                            <FacebookOutlined /> Facebook
                        </Option>
                        <Option value="instagram">
                            <InstagramOutlined /> Instagram
                        </Option>
                    </Select>
                    <Select
                        placeholder={t('Filter by status')}
                        allowClear
                        style={{ width: 150 }}
                        onChange={(value) => setFilters(prev => ({ ...prev, status: value || '' }))}
                    >
                        <Option value="pending">Pending</Option>
                        <Option value="replied">Replied</Option>
                        <Option value="converted">Converted</Option>
                        <Option value="archived">Archived</Option>
                    </Select>
                </Space>

                <Table
                    columns={columns}
                    dataSource={messagesData?.data || []}
                    loading={messagesLoading}
                    rowKey="_id"
                    pagination={{ pageSize: 20 }}
                    rowClassName={(record) => !record.isRead ? 'unread-message' : ''}
                />
            </Card>

            {/* Reply Modal */}
            <Modal
                title={`${t('Reply to')} ${selectedMessage?.senderName}`}
                visible={replyModalVisible}
                onCancel={() => {
                    setReplyModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                {selectedMessage && (
                    <>
                        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}>
                            <div style={{ marginBottom: 8 }}>
                                <strong>{t('Original Message')}:</strong>
                            </div>
                            <div>{selectedMessage.content}</div>
                            <div style={{ fontSize: '12px', color: '#888', marginTop: 8 }}>
                                {new Date(selectedMessage.timestamp).toLocaleString()}
                            </div>
                        </Card>

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSendReply}
                        >
                            <Form.Item
                                name="reply"
                                label={t('Your Reply')}
                                rules={[{ required: true, message: t('Please enter your reply') }]}
                            >
                                <TextArea
                                    rows={6}
                                    placeholder={t('Type your reply here...')}
                                    autoFocus
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SendOutlined />}
                                        loading={replyMutation.isLoading}
                                    >
                                        {t('Send Reply')}
                                    </Button>
                                    <Button onClick={() => {
                                        setReplyModalVisible(false);
                                        form.resetFields();
                                    }}>
                                        {t('Cancel')}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Modal>

            {/* Convert to Student Modal */}
            <Modal
                title={`${t('Convert')} ${selectedMessage?.senderName} ${t('to Student')}`}
                visible={convertModalVisible}
                onCancel={() => {
                    setConvertModalVisible(false);
                    studentForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={studentForm}
                    layout="vertical"
                    onFinish={handleConvertToStudent}
                >
                    <Form.Item
                        name="firstName"
                        label={t('First Name')}
                        rules={[{ required: true, message: t('Please enter first name') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="lastName"
                        label={t('Last Name')}
                        rules={[{ required: true, message: t('Please enter last name') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('Email')}
                        rules={[
                            { type: 'email', message: t('Please enter a valid email') }
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={t('Phone')}
                        rules={[{ required: true, message: t('Please enter phone number') }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="courseId"
                        label={t('Assign to Course (Optional)')}
                    >
                        <Select
                            placeholder={t('Select a course')}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                        >
                            {coursesData?.data?.map((course) => (
                                <Option key={course._id} value={course._id}>
                                    {course.name} - {course.fee?.amount} {course.fee?.currency}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<UserAddOutlined />}
                                loading={convertMutation.isLoading}
                            >
                                {t('Convert to Student')}
                            </Button>
                            <Button onClick={() => {
                                setConvertModalVisible(false);
                                studentForm.resetFields();
                            }}>
                                {t('Cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <style jsx global>{`
                .unread-message {
                    background-color: #e6f7ff !important;
                }
            `}</style>
        </div>
    );
};

export default SocialMessaging;
